import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, Plus, AlertTriangle } from "lucide-react";
import { Product } from "@/features/products/products-api";
import { getTeamsBy, updateTeam } from "@/features/supply-config/teams-api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MultiSelect } from "@/features/forms/ui/bundles-multi-select";
import { toast } from "@/hooks/use-toast";

// Gate before bundle creation: a product can only be launched in a region if at
// least one team there is mapped to it (Team↔Product is many-to-many). Shows the
// teams already mapped, lets the user attach more, or create a new team — then
// continue to bundle creation.
export default function LaunchProductDialog({
  product,
  regionCode,
  regionId,
  regionLabel,
  onClose,
}: {
  product: Product | null;
  regionCode: string;
  regionId?: string;
  regionLabel: string;
  onClose: () => void;
}) {
  const open = !!product;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [toAttach, setToAttach] = useState<string[]>([]);

  const { data: teams = [], isLoading } = useQuery({
    queryKey: ["teams", "by-region", regionId],
    queryFn: () => getTeamsBy({ region_id: regionId }),
    enabled: open && !!regionId,
  });

  const mapped = useMemo(
    () => teams.filter((t) => t.products?.some((p) => p.id === product?.id)),
    [teams, product],
  );
  const attachableOptions = useMemo(
    () =>
      teams
        .filter((t) => !t.products?.some((p) => p.id === product?.id))
        .map((t) => ({ label: t.name, value: t.id })),
    [teams, product],
  );
  const hasTeam = mapped.length > 0;

  const goToBundle = () => {
    onClose();
    navigate(`/${regionCode}/bundles/new?product_id=${product!.id}`);
  };

  const attach = useMutation({
    mutationFn: async () => {
      await Promise.all(
        toAttach.map((teamId) => {
          const team = teams.find((t) => t.id === teamId);
          const ids = [
            ...(team?.products?.map((p) => p.id) ?? []),
            product!.id,
          ];
          return updateTeam(teamId, { product_ids: ids });
        }),
      );
    },
    onSuccess: () => {
      setToAttach([]);
      qc.invalidateQueries({ queryKey: ["teams"] });
      toast({ title: "Teams attached to product" });
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  const createTeam = () => {
    onClose();
    navigate(`/${regionCode}/teams/new`, {
      state: {
        prefill: {
          product_ids: [product!.id],
          region_id: regionId,
          next: `/${regionCode}/bundles/new?product_id=${product!.id}`,
        },
      },
    });
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Launch {product.name}
            {regionLabel}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking teams…
          </div>
        ) : (
          <div className="space-y-5">
            {/* Currently mapped teams */}
            {hasTeam ? (
              <div className="space-y-2">
                <p className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  Teams mapped{regionLabel}
                </p>
                <div className="flex flex-wrap gap-2">
                  {mapped.map((t) => (
                    <Badge key={t.id} variant="secondary">
                      {t.name}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : (
              <p className="flex items-center gap-2 text-sm text-amber-600">
                <AlertTriangle className="h-4 w-4" />
                No team mapped to {product.name}
                {regionLabel} yet. Attach or create one first.
              </p>
            )}

            {/* Attach more teams (many-to-many) */}
            {attachableOptions.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">
                  {hasTeam ? "Attach more teams" : "Attach existing teams"}
                </p>
                <div className="flex gap-2">
                  <MultiSelect
                    options={attachableOptions}
                    value={toAttach}
                    onChange={setToAttach}
                    placeholder="Select teams"
                    className="flex-1 h-auto min-h-10 max-h-40 overflow-y-auto items-start py-2"
                  />
                  <Button
                    disabled={!toAttach.length || attach.isPending}
                    onClick={() => attach.mutate()}
                  >
                    {attach.isPending ? "Attaching…" : "Attach"}
                  </Button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <Button variant="outline" size="sm" onClick={createTeam}>
                <Plus className="h-4 w-4 mr-1" /> Create team{regionLabel}
              </Button>
              <Button disabled={!hasTeam} onClick={goToBundle}>
                Continue to bundle
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
